import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule } from 'ng-apexcharts';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';

import {
  StatsAiOverviewDTO,
} from '@solverml/api';

import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexTitleSubtitle,
  ApexPlotOptions,
  ApexDataLabels,
  ApexTooltip,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexStroke,
  ApexFill
} from 'ng-apexcharts';

// Definición de tipos para ApexCharts
export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis?: ApexXAxis;
  yaxis?: ApexYAxis;
  title?: ApexTitleSubtitle;
  plotOptions?: ApexPlotOptions;
  dataLabels?: ApexDataLabels;
  tooltip?: ApexTooltip;
  legend?: ApexLegend;
  stroke?: ApexStroke;
  fill?: ApexFill;
  labels?: string[];
  colors?: string[];
};

type SavesByTimeInterval = {
  interval_label: string;
  percentage: number;
  saves: number;
  goals_against: number;
  total_shots_against: number;
};

type ThrowTypeByTimeInterval = {
  interval_label: string;
  throw_type_percentages: Record<string, number>;
  throw_type_counts?: Record<string, number>;
  total_throws?: number;
};

type CategoryDistribution = {
  value: string;
  total: number;
  goals: number;
  percentage: number;
};

type ThrowTypeBreakdown = {
  throw_type: string;
  total: number;
  goals: number;
  percentage: number;
};

export type StatsAiDashboardSection =
  | 'kpis'
  | 'throwTypeBreakdown'
  | 'throwTypeEfficiency'
  | 'modelPerformance'
  | 'defensePerformance'
  | 'throwTypeByTime'
  | 'opponentAttackPerformance'
  | 'opponentThrowBreakdown'
  | 'goalsByTime'
  | 'savesByTime';

@Component({
  selector: 'app-stats-ai-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    NgApexchartsModule
  ],
  templateUrl: './stats-ai-dashboard.component.html',
  styleUrls: ['./stats-ai-dashboard.component.scss']
})
export class StatsAiDashboardComponent implements OnChanges {
  @Input({ required: true }) stats!: StatsAiOverviewDTO;
  @Input() sections: StatsAiDashboardSection[] | null = null;
  @Input() singleMatchMode = false;

  // Opciones de las gráficas
  public offensiveGauge!: ChartOptions;
  public defensiveGauge!: ChartOptions;
  public savesGauge!: ChartOptions;
  public throwTypeChart!: ChartOptions;
  public throwTypeEfficiencyChart!: ChartOptions;
  public modelChart!: ChartOptions;
  public defenseChart!: ChartOptions;
  public opponentAttackChart!: ChartOptions;
  public opponentThrowBreakdownChart!: ChartOptions;
  public timeChart!: ChartOptions;
  public savesByTimeChart!: ChartOptions;
  public throwTypeByTimeChart!: ChartOptions;

  private readonly THROW_TYPE_COLOR_MAP: Record<string, string> = {
    '6m': '#E41A1C',
    '7m': '#4DAF4A',
    '9m': '#FF7F00',
    '10m': '#984EA3',
    'extremo': '#A65628',
    'contraataque': '#F781BF',
    'vaselina': '#66C2A5',
    'pivote': '#FFD166',
    'fly': '#2E4057',
    'segunda oleada': '#8D6A9F'
  };

  private readonly THROW_TYPE_FALLBACK_COLORS = [
    '#E41A1C',
    '#4DAF4A',
    '#FF7F00',
    '#984EA3',
    '#be5a31',
    '#F781BF',
    '#5365ea',
    '#FFD166',
    '#2E4057',
    '#8D6A9F'
  ];

  private readonly DEFAULT_SECTIONS: StatsAiDashboardSection[] = [
    'kpis',
    'throwTypeBreakdown',
    'throwTypeEfficiency',
    'modelPerformance',
    'defensePerformance',
    'throwTypeByTime',
    'opponentAttackPerformance',
    'opponentThrowBreakdown',
    'goalsByTime',
    'savesByTime'
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stats'] && this.stats) {
      this.initCharts();
    }
  }

  private initCharts() {
    this.initGauges();
    this.initThrowTypeChart();
    this.initThrowTypeEfficiencyChart();
    this.initModelChart();
    this.initDefenseChart();
    this.initOpponentAttackChart();
    this.initOpponentThrowBreakdownChart();
    this.initTimeChart();
    this.initSavesByTimeChart();
    this.initThrowTypeByTimeChart();
  }

  public hasSection(section: StatsAiDashboardSection): boolean {
    const enabledSections = this.sections ?? this.DEFAULT_SECTIONS;
    return enabledSections.includes(section);
  }

  private initGauges() {
    // Semi-donut gauge for offensive efficiency
    this.offensiveGauge = {
      series: [Math.round(this.stats.offensive_efficiency.percentage)],
      chart: {
        type: 'radialBar',
        height: 350,
        sparkline: {
          enabled: true
        }
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: "#e7e7e7",
            strokeWidth: '97%',
            margin: 5, // margin is in pixels
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              color: '#999',
              opacity: 1,
              blur: 2
            }
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              offsetY: -2,
              fontSize: '22px',
              formatter: (val) => val + "%"
            }
          }
        }
      },
      fill: {
        colors: ['#28a745']
      },
      labels: ['Eficacia Ofensiva']
    };

    // Semi-donut gauge for defensive efficiency
    this.defensiveGauge = {
      series: [Math.round(this.stats.defensive_saves.percentage)],
      chart: {
        type: 'radialBar',
        height: 350,
        sparkline: {
          enabled: true
        }
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: "#e7e7e7",
            strokeWidth: '97%',
            margin: 5,
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              color: '#999',
              opacity: 1,
              blur: 2
            }
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              offsetY: -2,
              fontSize: '22px',
              formatter: (val) => val + "%"
            }
          }
        }
      },
      fill: {
        colors: ['#0d6efd']
      },
      labels: ['Eficacia Defensiva (No Gol)']
    };

    const goalkeepingTotals = this.getGoalkeepingTotals();
    const savesPercentage = goalkeepingTotals.totalShots > 0
      ? (goalkeepingTotals.totalSaves / goalkeepingTotals.totalShots) * 100
      : 0;
    this.savesGauge = {
      series: [Math.round(savesPercentage)],
      chart: {
        type: 'radialBar',
        height: 350,
        sparkline: {
          enabled: true
        }
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: "#e7e7e7",
            strokeWidth: '97%',
            margin: 5,
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              color: '#999',
              opacity: 1,
              blur: 2
            }
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              offsetY: -2,
              fontSize: '22px',
              formatter: (val) => val + "%"
            }
          }
        }
      },
      fill: {
        colors: ['#20c997']
      },
      labels: ['Eficacia Paradas']
    };
  }

  private initThrowTypeChart() {
    const data = this.stats.throw_type_breakdown || [];
    const categories = data.map(d => d.throw_type);
    const goals = data.map(d => d.goals);
    const failures = data.map(d => d.total - d.goals);

    this.throwTypeChart = {
      series: [
        { name: 'Goles', data: goals, color: '#28a745' },
        { name: 'Fallos', data: failures, color: '#dc3545' }
      ],
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
        }
      },
      xaxis: {
        categories: categories,
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opt) => {
          // Si el valor es pequeño, no pintar label
          if(typeof val === 'number' && val === 0) return '';
          return val.toString();
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initModelChart() {
    const data = this.stats.modelo_juego_distribution || [];
    const categories = data.map(d => d.value);
    const efficiency = data.map(d => Number(d.percentage.toFixed(2)));
    const absoluteRatios = data.map(d => ({ successful: d.goals, total: d.total }));

    this.modelChart = {
      series: [
        { name: 'Eficacia (%)', data: efficiency, color: '#17a2b8' }
      ],
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4
        }
      },
      xaxis: {
        categories: categories,
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`
      },
      tooltip: this.createSingleSeriesAbsoluteTooltip(absoluteRatios),
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initDefenseChart() {
    const data = this.stats.defense_type_distribution || [];
    const categories = data.map(d => d.value);
    const goalsPct = data.map(d => Number(d.percentage.toFixed(2)));
    const noGoalPct = data.map(d => Number((100 - d.percentage).toFixed(2)));
    const goalNoGoalRatios = data.map(d => ({ goals: d.goals, total: d.total }));

    this.defenseChart = {
      series: [
        { name: 'Gol (%)', data: goalsPct, color: '#28a745' },
        { name: 'No Gol (%)', data: noGoalPct, color: '#dc3545' }
      ],
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: false,
        }
      },
      xaxis: {
        categories: categories,
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          const numericValue = Array.isArray(val) ? Number(val[0] ?? 0) : Number(val);
          return numericValue > 0 ? `${numericValue.toFixed(1)}%` : '';
        }
      },
      tooltip: this.createGoalNoGoalAbsoluteTooltip(goalNoGoalRatios, 0),
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initOpponentAttackChart() {
    const data = this.getOpponentAttackTypeDistribution();
    const categories = data.map(d => d.value);
    const goalsPct = data.map(d => Number(d.percentage.toFixed(2)));
    const noGoalPct = data.map(d => Number((100 - d.percentage).toFixed(2)));
    const goalNoGoalRatios = data.map(d => ({ goals: d.goals, total: d.total }));

    this.opponentAttackChart = {
      series: [
        { name: 'No Gol (%)', data: noGoalPct, color: '#28a745' },
        { name: 'Gol (%)', data: goalsPct, color: '#dc3545' }
      ],
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: false
        }
      },
      xaxis: {
        categories
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          const numericValue = Array.isArray(val) ? Number(val[0] ?? 0) : Number(val);
          return numericValue > 0 ? `${numericValue.toFixed(1)}%` : '';
        }
      },
      tooltip: this.createGoalNoGoalAbsoluteTooltip(goalNoGoalRatios, 1),
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initOpponentThrowBreakdownChart() {
    const data = this.getOpponentThrowTypeBreakdown();
    const categories = data.map(d => d.throw_type);
    const goalsPct = data.map(d => Number(d.percentage.toFixed(2)));
    const noGoalPct = data.map(d => Number((100 - d.percentage).toFixed(2)));
    const goalNoGoalRatios = data.map(d => ({ goals: d.goals, total: d.total }));

    this.opponentThrowBreakdownChart = {
      series: [
        { name: 'No Gol (%)', data: noGoalPct, color: '#28a745' },
        { name: 'Gol (%)', data: goalsPct, color: '#dc3545' }
      ],
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: false
        }
      },
      xaxis: {
        categories,
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          const numericValue = Array.isArray(val) ? Number(val[0] ?? 0) : Number(val);
          return numericValue > 0 ? `${numericValue.toFixed(1)}%` : '';
        }
      },
      tooltip: this.createGoalNoGoalAbsoluteTooltip(goalNoGoalRatios, 1),
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initTimeChart() {
    const data = this.stats.goals_by_time_interval || [];
    const categories = data.map(d => d.interval_label);
    const goalsFor = data.map(d => d.goals_for);
    const goalsAgainst = data.map(d => d.goals_against);

    this.timeChart = {
      series: [
        { name: 'Goles a Favor', data: goalsFor, color: '#28a745' },
        { name: 'Goles en Contra', data: goalsAgainst, color: '#dc3545' }
      ],
      chart: {
        type: 'area',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' },
      xaxis: {
        categories: categories,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initThrowTypeEfficiencyChart() {
    const data = this.stats.throw_type_breakdown || [];
    const categories = data.map(d => d.throw_type);
    const efficiency = data.map(d => d.percentage);
    const absoluteRatios = data.map(d => ({ successful: d.goals, total: d.total }));

    this.throwTypeEfficiencyChart = {
      series: [
        { name: 'Eficacia (%)', data: efficiency, color: '#0d6efd' }
      ],
      chart: {
        type: 'bar',
        height: 320,
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      xaxis: {
        categories
      },
      tooltip: this.createSingleSeriesAbsoluteTooltip(absoluteRatios),
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initSavesByTimeChart() {
    const data = this.getSavesByTimeInterval();
    const categories = data.map(d => d.interval_label);
    const efficiency = data.map(d => d.percentage);
    const absoluteRatios = data.map(d => ({ successful: d.saves, total: d.total_shots_against }));

    this.savesByTimeChart = {
      series: [
        { name: 'Eficacia de Paradas (%)', data: efficiency, color: '#20c997' }
      ],
      chart: {
        type: 'line',
        height: 320,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      xaxis: {
        categories
      },
      tooltip: this.createSingleSeriesAbsoluteTooltip(absoluteRatios),
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  private initThrowTypeByTimeChart() {
    const data = this.getThrowTypeByTimeInterval();
    const categories = data.map(d => d.interval_label);

    const throwTypeSet = new Set<string>();
    data.forEach(d => {
      Object.keys(d.throw_type_percentages || {}).forEach(k => throwTypeSet.add(k));
    });
    const throwTypes = Array.from(throwTypeSet).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

    const intervalAbsoluteData = data.map(interval => {
      const countsByType = interval.throw_type_counts || {};
      const configuredTotal = Number(interval.total_throws || 0);
      const computedTotalFromCounts = Object.values(countsByType).reduce((acc, count) => acc + Number(count || 0), 0);
      const effectiveTotal = configuredTotal > 0 ? configuredTotal : computedTotalFromCounts;

      if (effectiveTotal > 0) {
        return {
          countsByThrowType: countsByType,
          total: effectiveTotal
        };
      }

      return this.inferThrowTypeCounts(interval.throw_type_percentages || {}, throwTypes);
    });

    // Normaliza cada franja para que el apilado siempre represente 100%.
    // Esto evita intervalos con un único tipo mostrando <100% por efecto de medias previas.
    const normalizedByInterval = data.map(interval => {
      const raw = interval.throw_type_percentages || {};
      const total = Object.values(raw).reduce((acc, value) => acc + Number(value || 0), 0);
      const normalized: Record<string, number> = {};
      for (const throwType of throwTypes) {
        const value = Number(raw[throwType] || 0);
        normalized[throwType] = total > 0 ? Number(((value / total) * 100).toFixed(2)) : 0;
      }
      return normalized;
    });

    const series = throwTypes.map(throwType => ({
      name: throwType,
      data: normalizedByInterval.map(interval => interval[throwType] || 0),
      color: this.getThrowTypeColor(throwType)
    }));

    const seriesColors = throwTypes.map(throwType => this.getThrowTypeColor(throwType));

    this.throwTypeByTimeChart = {
      series,
      chart: {
        type: 'bar',
        stacked: true,
        height: 360,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      plotOptions: {
        bar: {
          horizontal: false
        }
      },
      dataLabels: {
        enabled: false
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`
        }
      },
      tooltip: this.createThrowTypeByTimeAbsoluteTooltip(throwTypes, intervalAbsoluteData),
      xaxis: {
        categories
      },
      colors: seriesColors,
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    };
  }

  public getSavesByTimeInterval(): SavesByTimeInterval[] {
    return ((this.stats as any)?.saves_by_time_interval || []) as SavesByTimeInterval[];
  }

  public getOpponentAttackTypeDistribution(): CategoryDistribution[] {
    return ((this.stats as any)?.opponent_attack_type_distribution || []) as CategoryDistribution[];
  }

  public getOpponentThrowTypeBreakdown(): ThrowTypeBreakdown[] {
    return ((this.stats as any)?.opponent_throw_type_breakdown || []) as ThrowTypeBreakdown[];
  }

  public getGoalkeepingTotals(): { totalSaves: number; totalShots: number; percentage: number } {
    const intervals = this.getSavesByTimeInterval();
    const totalSaves = intervals.reduce((acc, item) => acc + Number(item.saves || 0), 0);
    const totalShots = intervals.reduce((acc, item) => acc + Number(item.total_shots_against || 0), 0);
    const percentage = totalShots > 0 ? (totalSaves / totalShots) * 100 : 0;
    return { totalSaves, totalShots, percentage };
  }

  public getThrowTypeByTimeInterval(): ThrowTypeByTimeInterval[] {
    return ((this.stats as any)?.throw_type_by_time_interval || []) as ThrowTypeByTimeInterval[];
  }

  private normalizeThrowType(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private hashString(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
      hash = ((hash << 5) - hash) + value.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  private getThrowTypeColor(throwType: string): string {
    const normalized = this.normalizeThrowType(throwType);
    const mappedColor = this.THROW_TYPE_COLOR_MAP[normalized];
    if (mappedColor) {
      return mappedColor;
    }
    const paletteIndex = this.hashString(normalized || throwType) % this.THROW_TYPE_FALLBACK_COLORS.length;
    return this.THROW_TYPE_FALLBACK_COLORS[paletteIndex];
  }

  private formatPercent(value: number): string {
    return `${Number(value).toFixed(1)}%`;
  }

  private formatPercentWithAbsolute(value: number, successful: number, total: number): string {
    const safeTotal = Number(total || 0);
    const safeSuccessful = Number(successful || 0);

    if (safeTotal <= 0) {
      return this.formatPercent(value);
    }

    return `${this.formatPercent(value)} (${safeSuccessful}/${safeTotal})`;
  }

  private createSingleSeriesAbsoluteTooltip(
    absoluteRatios: Array<{ successful: number; total: number }>
  ): ApexTooltip {
    return {
      y: {
        formatter: (val: number, opts?: any) => {
          const dataPointIndex = Number(opts?.dataPointIndex ?? -1);
          const ratio = absoluteRatios[dataPointIndex];
          if (!ratio) {
            return this.formatPercent(Number(val));
          }
          return this.formatPercentWithAbsolute(Number(val), ratio.successful, ratio.total);
        }
      }
    };
  }

  private createGoalNoGoalAbsoluteTooltip(
    absoluteRatios: Array<{ goals: number; total: number }>,
    goalSeriesIndex: number
  ): ApexTooltip {
    return {
      y: {
        formatter: (val: number, opts?: any) => {
          const dataPointIndex = Number(opts?.dataPointIndex ?? -1);
          const seriesIndex = Number(opts?.seriesIndex ?? -1);
          const ratio = absoluteRatios[dataPointIndex];

          if (!ratio) {
            return this.formatPercent(Number(val));
          }

          const successful = seriesIndex === goalSeriesIndex
            ? Number(ratio.goals || 0)
            : Math.max(Number(ratio.total || 0) - Number(ratio.goals || 0), 0);

          return this.formatPercentWithAbsolute(Number(val), successful, Number(ratio.total || 0));
        }
      }
    };
  }

  private createThrowTypeByTimeAbsoluteTooltip(
    throwTypes: string[],
    inferredIntervalCounts: Array<{ countsByThrowType: Record<string, number>; total: number } | null>
  ): ApexTooltip {
    return {
      y: {
        formatter: (val: number, opts?: any) => {
          const dataPointIndex = Number(opts?.dataPointIndex ?? -1);
          const seriesIndex = Number(opts?.seriesIndex ?? -1);
          const throwType = throwTypes[seriesIndex];
          const intervalCounts = inferredIntervalCounts[dataPointIndex];

          if (!intervalCounts || !throwType) {
            return this.formatPercentWithAbsolute(
              Number(val),
              Math.max(Math.round(Number(val)), 0),
              100
            );
          }

          const successful = Number(intervalCounts.countsByThrowType[throwType] || 0);
          return this.formatPercentWithAbsolute(Number(val), successful, intervalCounts.total);
        }
      }
    };
  }

  private inferThrowTypeCounts(
    percentagesByType: Record<string, number>,
    throwTypes: string[]
  ): { countsByThrowType: Record<string, number>; total: number } | null {
    const values = throwTypes.map(throwType => Number(percentagesByType[throwType] || 0));
    const hasAnyValue = values.some(value => value > 0);
    if (!hasAnyValue) {
      return null;
    }

    const maxTotal = 80;
    let bestResult: { countsByThrowType: Record<string, number>; total: number; error: number } | null = null;

    for (let total = 1; total <= maxTotal; total += 1) {
      const counts = values.map(value => Math.round((value / 100) * total));
      const countsSum = counts.reduce((acc, count) => acc + count, 0);
      if (countsSum !== total) {
        continue;
      }

      const totalError = counts.reduce((acc, count, index) => {
        const reconstructed = (count / total) * 100;
        return acc + Math.abs(reconstructed - values[index]);
      }, 0);

      const maxError = counts.reduce((acc, count, index) => {
        const reconstructed = (count / total) * 100;
        return Math.max(acc, Math.abs(reconstructed - values[index]));
      }, 0);

      if (maxError > 0.6 || totalError > 1.8) {
        continue;
      }

      const countsByThrowType: Record<string, number> = {};
      throwTypes.forEach((throwType, index) => {
        countsByThrowType[throwType] = counts[index];
      });

      if (!bestResult || totalError < bestResult.error) {
        bestResult = { countsByThrowType, total, error: totalError };
      }
    }

    if (!bestResult) {
      return null;
    }

    return {
      countsByThrowType: bestResult.countsByThrowType,
      total: bestResult.total
    };
  }
}
